/**
 * SelectorArrayEditor Component
 *
 * Reusable component for editing an array of selectors.
 * Each selector has a type (css/xpath) and a value.
 *
 * @param {Object} props
 * @param {Array} props.selectors - Array of {type: "css"|"xpath", value: string}
 * @param {Function} props.onChange - Called with updated selectors array
 * @param {string} props.label - Label for the section
 * @param {string} [props.description] - Optional helper text
 * @param {string} [props.emptyMessage] - Message when no selectors exist
 */

import React from "react";
import { FiPlus, FiX } from "react-icons/fi";

const SelectorArrayEditor = ({
  selectors = [],
  onChange,
  label,
  description,
  emptyMessage = "No selectors defined. Add a selector to get started.",
}) => {
  const addSelector = () => {
    onChange([...selectors, { type: "css", value: "" }]);
  };

  const removeSelector = (index) => {
    const updated = selectors.filter((_, i) => i !== index);
    onChange(updated);
  };

  const updateSelector = (index, field, value) => {
    const updated = [...selectors];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm text-gray-700 dark:text-gray-200">{label}</label>
          {description && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{description}</p>
          )}
        </div>
        <button
          onClick={addSelector}
          className="px-2 py-1 rounded text-xs font-medium
                   bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30
                   hover:bg-blue-200 dark:hover:bg-blue-500/30 hover:border-blue-300 dark:hover:border-blue-400/50
                   transition-colors flex items-center gap-1"
        >
          <FiPlus className="w-3 h-3" />
          Add
        </button>
      </div>

      {/* Selectors List */}
      <div className="space-y-2">
        {selectors.length === 0 ? (
          <div className="p-3 bg-gray-50 dark:bg-gray-800/30 border border-dashed border-gray-300 dark:border-gray-700 rounded text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">{emptyMessage}</p>
          </div>
        ) : (
          selectors.map((selector, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/30
                         rounded hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
            >
              {/* Type Dropdown */}
              <div className="relative">
                <select
                  value={selector.type === "xpath" ? "xpath" : "css"}
                  onChange={(e) => updateSelector(index, "type", e.target.value)}
                  className="appearance-none pl-2 pr-6 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded
                             text-xs text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-blue-400
                             cursor-pointer"
                >
                  <option value="css">CSS</option>
                  <option value="xpath">XPath</option>
                </select>
                <svg className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Value Input */}
              <input
                type="text"
                value={selector.value || ""}
                onChange={(e) => updateSelector(index, "value", e.target.value)}
                placeholder={selector.type === "xpath" ? '//div[@class="item"]' : "div.item"}
                className="flex-1 px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600
                           rounded text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500
                           focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 font-mono"
              />

              {/* Remove Button */}
              <button
                onClick={() => removeSelector(index)}
                className="p-1 rounded text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300
                           hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SelectorArrayEditor;
